/**
 * DuelStage — M2's in-world battle staging director (TODOS #25,
 * litigation ruling 2026-08-02). Owns the intro/outro theater only:
 * the lead's battler sprite stepping into the gap between player and
 * wild, the camera punch-in on the fixed midpoint, and the restore.
 * It does NOT run the battle — the duel host drives
 * runInteractiveBattle between intro() and outro().
 *
 * Staging contract (locked in the 05-11 design doc):
 *   - The follower IS the battler: WorldScene hides the follower
 *     (onSleep — "out fighting" is semantically a sleep) and this
 *     class walks a duel-owned sprite from the follower's tile into
 *     the slot findDuelSlot picked. Same sheet, same frames — the
 *     creature that walks behind you is the creature that fights.
 *   - Camera: stopFollow, punch to base×1.45 centered on the FIXED
 *     midpoint between the two home tiles. Computed once; lunges
 *     never move the camera. Outro reverses and re-follows.
 *   - The wild's sprite stays owned by RoamerSystem, frozen on its
 *     tile (roamer ticking is gated for the whole engagement).
 */
import Phaser from 'phaser';
import {
  OW_IDLE_FRAME,
  OW_WALK_FRAMES,
  encodedDirection,
} from './follower';
import type { Facing } from './followerState';
import {
  DUEL_FRAME_Y,
  duelMidpoint,
  duelZoom,
  findDuelSlot,
  snapPinchZoom,
} from './duelStage';

export interface OpponentBattlerParams {
  species: string;
  /** Where the opponent's lead walks FROM — the trainer's tile. */
  fromTile: { x: number; y: number };
  /** Its duel slot (findTrainerDuelSlots.opp). */
  slot: { x: number; y: number };
}

/** Battler's step-forward cadence — brisker than the roamer amble
 *  (it's answering a call), calmer than the player sprint. */
const BATTLER_STEP_MS = 200;
const CAM_PUNCH_MS = 450;
const CAM_RESTORE_MS = 350;
const BATTLER_DEPTH = 5;
// DUEL_FRAME_Y (vertical framing bias) lives in duelStage.ts now —
// the zoom guardrail needs it to budget the shorter top half.

export interface DuelIntroParams {
  playerTile: { x: number; y: number };
  /** The opposing side's anchor tile: the wild roamer's tile, or the
   *  trainer duel's opposing SLOT (when `opponent` is provided). */
  wildTile: { x: number; y: number };
  leadSpecies: string;
  /** Where the battler walks FROM — the follower's visible home tile,
   *  or null (hidden/absent follower) to materialize on the player's
   *  tile and step out from there. */
  fromTile: { x: number; y: number } | null;
  isBlocked: (x: number, y: number) => boolean;
  /** My lead's slot, precomputed (trainer duels). Omitted → the wild
   *  path's findDuelSlot runs internally. */
  mySlot?: { x: number; y: number };
  /** Trainer duels: the opposing lead as a second DUEL-OWNED battler
   *  (wild duels' opponent is the roamer's own sprite instead). */
  opponent?: OpponentBattlerParams;
}

export class DuelStage {
  private scene: Phaser.Scene;
  private tileSize: number;
  private battler: Phaser.GameObjects.Sprite | null = null;
  private opponentBattler: Phaser.GameObjects.Sprite | null = null;
  private baseZoom = 0;
  private followTarget: Phaser.GameObjects.Sprite;
  /** Where each battler walked out FROM — the outro walks the living
   *  ones back home before they vanish (owner ask 2026-08-03: no
   *  snap-back). */
  private battlerFrom: { x: number; y: number } | null = null;
  private opponentFrom: { x: number; y: number } | null = null;
  /** The fixed camera midpoint — stored so live zoom changes (pinch)
   *  can recenter without recomputing staging. */
  private mid: { x: number; y: number } | null = null;
  /** The lead's home tile for this duel — sim lunges leave and return
   *  here (Shape C: grid-locked at rest). */
  slotTile: { x: number; y: number } | null = null;
  /** Opposing lead's home tile (trainer duels; wilds: the roamer tile). */
  oppSlotTile: { x: number; y: number } | null = null;

  constructor(
    scene: Phaser.Scene,
    tileSize: number,
    followTarget: Phaser.GameObjects.Sprite,
  ) {
    this.scene = scene;
    this.tileSize = tileSize;
    this.followTarget = followTarget;
  }

  getSprites(): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    if (this.battler) out.push(this.battler);
    if (this.opponentBattler) out.push(this.opponentBattler);
    return out;
  }

  getBattler(): Phaser.GameObjects.Sprite | null {
    return this.battler;
  }

  getOpponentBattler(): Phaser.GameObjects.Sprite | null {
    return this.opponentBattler;
  }

  /** Stage the duel: battler walks into the slot, camera punches in.
   *  Resolves when both movements settle. */
  async intro(params: DuelIntroParams): Promise<{ slot: { x: number; y: number } }> {
    const t = this.tileSize;
    const slot =
      params.mySlot ??
      findDuelSlot(params.playerTile, params.wildTile, params.isBlocked);
    this.slotTile = slot;
    const oppSlot = params.opponent?.slot ?? params.wildTile;
    this.oppSlotTile = oppSlot;

    const from = params.fromTile ?? params.playerTile;
    const sheet = `follower-ow:${params.leadSpecies}`;
    this.battler = this.scene.add
      .sprite((from.x + 0.5) * t, (from.y + 1) * t, sheet, OW_IDLE_FRAME.down)
      .setOrigin(0.5, 1)
      .setDepth(BATTLER_DEPTH);
    if (params.opponent) {
      const oppFrom = params.opponent.fromTile;
      this.opponentBattler = this.scene.add
        .sprite(
          (oppFrom.x + 0.5) * t,
          (oppFrom.y + 1) * t,
          `follower-ow:${params.opponent.species}`,
          OW_IDLE_FRAME.down,
        )
        .setOrigin(0.5, 1)
        .setDepth(BATTLER_DEPTH);
    }

    this.battlerFrom = { ...from };
    if (params.opponent) this.opponentFrom = { ...params.opponent.fromTile };

    const cam = this.scene.cameras.main;
    this.baseZoom = cam.zoom;
    cam.stopFollow();
    const mid = duelMidpoint(slot, this.oppSlotTile, t);
    this.mid = mid;
    // Pinch-tested zoom preference (duelPinch persists the multiplier)
    // overrides the default factor — the guardrail deliberately does
    // NOT apply to an explicit hand-tuned value. Crispness still
    // does: the resting zoom snaps to an integer either way, or the
    // NEAREST-filtered texels land fuzzy (the very thing being tuned).
    const storedMult = Number.parseFloat(
      globalThis.localStorage?.getItem('poke-duel-zoom') ?? '',
    );
    const targetZoom =
      Number.isFinite(storedMult) && storedMult >= 1
        ? Math.max(this.baseZoom, Math.round(this.baseZoom * storedMult))
        : duelZoom(this.baseZoom, cam.width, cam.height, t, {
            dx: Math.abs(slot.x - oppSlot.x),
            dy: Math.abs(slot.y - oppSlot.y),
          });

    const walk = Promise.all([
      this.walkSpriteTo(this.battler, slot, this.oppSlotTile),
      params.opponent && this.opponentBattler
        ? this.walkSpriteTo(this.opponentBattler, params.opponent.slot, slot)
        : Promise.resolve(),
    ]);
    // Phaser convention: the camera zooms around its midPoint, which is
    // scroll + viewport/2 in WORLD px — zoom does NOT enter the
    // centering math. (Dividing by zoom here shoved the first dogfood
    // duel's view into the border forest, clamped at the bounds edge.)
    const punch = new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: cam,
        zoom: targetZoom,
        scrollX: mid.x - cam.width / 2,
        // midPoint convention on X; Y biases the pair into the upper
        // third so the chrome frames below them (DUEL_FRAME_Y). The
        // midPoint sits at viewport/2, so shifting the target by
        // (0.5 − bias)·height/zoom world-px raises the pair on screen.
        scrollY: mid.y - cam.height / 2 + ((0.5 - DUEL_FRAME_Y) * cam.height) / targetZoom,
        duration: CAM_PUNCH_MS,
        ease: 'Sine.easeInOut',
        onComplete: () => resolve(),
      });
    });
    await Promise.all([walk, punch]);
    return { slot };
  }

  /** Reverse the theater: the LIVING battlers walk back to where they
   *  came from (mine to the follower's tile, a trainer's to their
   *  trainer) while the camera eases home — then they vanish and the
   *  follower re-materializes via onWake. Fainted battlers skip the
   *  walk (nothing marches home at 0 HP). */
  async outro(opts?: { recallBattlerInPlace?: boolean }): Promise<void> {
    const cam = this.scene.cameras.main;
    const walkBacks: Promise<void>[] = [];
    if (this.battler && this.battler.alpha > 0.5 && this.battlerFrom) {
      // Player-moved stagings (Cornered rungs 2–3): the walk-home
      // target is the player's own post-move tile, so marching there
      // re-creates the overlap this whole feature exists to kill.
      // Recall in place instead — same shrink-into-the-ball envelope
      // as the follower's cinematic recall; the follower then
      // re-materializes beside the player (established HGSS grammar).
      walkBacks.push(
        opts?.recallBattlerInPlace
          ? this.recallInPlace(this.battler)
          : this.walkSpriteTo(this.battler, this.battlerFrom, this.battlerFrom),
      );
    }
    if (
      this.opponentBattler &&
      this.opponentBattler.alpha > 0.5 &&
      this.opponentFrom
    ) {
      walkBacks.push(
        this.walkSpriteTo(this.opponentBattler, this.opponentFrom, this.opponentFrom),
      );
    }
    await Promise.all([
      ...walkBacks,
      new Promise<void>((resolve) => {
        this.scene.tweens.add({
          targets: cam,
          zoom: this.baseZoom || cam.zoom,
          // midPoint convention — see intro().
          scrollX: this.followTarget.x - cam.width / 2,
          scrollY: this.followTarget.y - cam.height / 2,
          duration: CAM_RESTORE_MS,
          ease: 'Sine.easeInOut',
          onComplete: () => resolve(),
        });
      }),
    ]);
    cam.startFollow(this.followTarget, true);
    this.battler?.destroy();
    this.battler = null;
    this.opponentBattler?.destroy();
    this.opponentBattler = null;
    this.slotTile = null;
    this.oppSlotTile = null;
    this.battlerFrom = null;
    this.opponentFrom = null;
    this.mid = null;
  }

  /** Live zoom for the pinch gesture: re-applies zoom around the
   *  stored midpoint with the DUEL_FRAME_Y framing bias. The dial has
   *  integer-zoom DETENTS (snapPinchZoom): every stop the camera can
   *  rest on is crisp, so every number the pill reads out is directly
   *  bakeable. Returns the snapped multiplier actually applied
   *  (1×–6× over base). */
  setZoomMultiplier(mult: number): number {
    return this.applyPinchZoom((base) => snapPinchZoom(base, mult));
  }

  /** Wheel/trackpad path: step to the ADJACENT integer zoom stop.
   *  (A fixed ratio step like ×1.06 can't escape a detent at small
   *  base zooms — round(base·mult·1.06) lands back on the same
   *  integer forever.) Returns the applied multiplier. */
  stepZoom(direction: 1 | -1): number {
    return this.applyPinchZoom((base, cam) =>
      Math.min(base * 6, Math.max(base, Math.round(cam.zoom) + direction)),
    );
  }

  private applyPinchZoom(
    pick: (baseZoom: number, cam: Phaser.Cameras.Scene2D.Camera) => number,
  ): number {
    const cam = this.scene.cameras.main;
    if (!this.mid || !this.baseZoom) return cam.zoom / (this.baseZoom || 1);
    const zoom = pick(this.baseZoom, cam);
    cam.setZoom(zoom);
    cam.scrollX = this.mid.x - cam.width / 2;
    cam.scrollY =
      this.mid.y - cam.height / 2 + ((0.5 - DUEL_FRAME_Y) * cam.height) / zoom;
    return zoom / this.baseZoom;
  }

  getZoomMultiplier(): number {
    const cam = this.scene.cameras.main;
    return this.baseZoom ? cam.zoom / this.baseZoom : 1;
  }

  /** Emergency teardown (scene shutdown mid-duel): no tweens, just
   *  release everything so nothing leaks or double-renders. */
  dispose(): void {
    this.battler?.destroy();
    this.battler = null;
    this.opponentBattler?.destroy();
    this.opponentBattler = null;
    this.slotTile = null;
    this.oppSlotTile = null;
  }

  /** Recall shrink: scale-to-feet + fade over 280ms Back.easeIn —
   *  the follower recall envelope (follower.ts), so it reads as
   *  "back into the ball" rather than a despawn. */
  private async recallInPlace(b: Phaser.GameObjects.Sprite): Promise<void> {
    await new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: b,
        scale: 0,
        alpha: 0,
        duration: 280,
        ease: 'Back.easeIn',
        onComplete: () => resolve(),
      });
    });
  }

  /** Walk a duel-owned sprite from its current position to `slot`,
   *  one tile per BATTLER_STEP_MS along the dominant axis, with
   *  walk-pair frame alternation; faces `opposite` at arrival. */
  private async walkSpriteTo(
    b: Phaser.GameObjects.Sprite | null,
    slot: { x: number; y: number },
    opposite: { x: number; y: number },
  ): Promise<void> {
    if (!b) return;
    const t = this.tileSize;
    const targetX = (slot.x + 0.5) * t;
    const targetY = (slot.y + 1) * t;
    const tiles = Math.max(
      Math.abs(targetX - b.x) / t,
      Math.abs(targetY - b.y) / t,
    );
    const facing = this.facingToward(b.x, b.y, targetX, targetY);
    const enc = encodedDirection(facing);
    b.setFlipX(facing === 'right');
    if (tiles > 0.01) {
      // Walk-pair alternation on a timer while the position tween runs.
      let step = 0;
      const frameTimer = this.scene.time.addEvent({
        delay: BATTLER_STEP_MS / 2,
        loop: true,
        callback: () => {
          const pair = OW_WALK_FRAMES[enc];
          b.setFrame(step % 2 === 0 ? pair[0] : pair[1]);
          step++;
        },
      });
      await new Promise<void>((resolve) => {
        this.scene.tweens.add({
          targets: b,
          x: targetX,
          y: targetY,
          duration: Math.max(BATTLER_STEP_MS, tiles * BATTLER_STEP_MS),
          ease: 'Linear',
          onComplete: () => resolve(),
        });
      });
      frameTimer.remove();
    }
    const faceOpp = this.facingToward(
      b.x,
      b.y,
      (opposite.x + 0.5) * t,
      (opposite.y + 1) * t,
    );
    b.setFlipX(faceOpp === 'right');
    b.setFrame(OW_IDLE_FRAME[encodedDirection(faceOpp)]);
  }

  private facingToward(fromX: number, fromY: number, toX: number, toY: number): Facing {
    const dx = toX - fromX;
    const dy = toY - fromY;
    if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
    return dy >= 0 ? 'down' : 'up';
  }
}
